import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import type { Receipt } from '@/api/client'
import { pickableProperties } from '@/lib/property-lifecycle'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { categoryLabel, formatDate } from './supplies-dashboard'
import { shortItemName } from './supplies'

/**
 * Unmatched receipts (CAP-496): the receipts admin-api could not put on a
 * house, with everything an operator needs to decide (who it shipped to,
 * where, what was in it, the receipt text), a house picker and a Match
 * button. Matching calls POST /admin/receipts/:id/attribute; the resolver
 * never overrides a manual pin (CAP-460).
 *
 * Receipts shipped to Andrei's home or a cleaner's are ignored by decision
 * and only counted here, never listed (CAP-495).
 */

/** How the API names an ignored reason, and how the page says it. */
const IGNORED_LABELS: Record<string, (n: number) => string> = {
  home: n => `${n} to Andrei's home`,
  crew_stock: n => `${n} crew stock`,
  skipped: n => `${n} skipped by hand`,
}

export function ignoredSentence(ignored: Record<string, number>): string | null {
  const parts = Object.entries(ignored)
    .filter(([, n]) => n > 0)
    .map(([reason, n]) => (IGNORED_LABELS[reason] ?? (k => `${k} ${reason}`))(n))
  if (parts.length === 0) return null
  return `Skipped on purpose: ${parts.join(', ')}.`
}

const REASON_LABELS: Record<string, string> = {
  no_signal: 'No street address on the receipt',
  no_match: 'Address matches no house',
  ambiguous: 'Address fits more than one house',
}

export function reasonLabel(reason: string | null | undefined): string {
  return (reason && REASON_LABELS[reason]) || 'Not matched'
}

/** "Shipped to Rebecca Allison, HOUSTON, TX", or a plain sentence when the receipt says nothing. */
export function shipTo(r: Pick<Receipt, 'delivery_recipient' | 'delivery_address'>): string {
  const parts = [r.delivery_recipient, r.delivery_address].filter((p): p is string =>
    Boolean(p && p.trim())
  )
  return parts.length ? `Shipped to ${parts.join(', ')}` : 'No delivery details on the receipt'
}

export interface House {
  id: string
  name: string
}

/** The active houses, by name, for any picker that pins a receipt. */
export function useHouses(): House[] {
  const properties = useQuery({
    queryKey: ['properties', 'active'],
    queryFn: () => api.getProperties(),
    staleTime: 5 * 60_000,
  })
  // A property still onboarding has no supplies to pin a receipt to (CAP-1467).
  return pickableProperties(properties.data?.properties ?? [])
    .flatMap(p => (p.id ? [{ id: p.id, name: p.name ?? p.id }] : []))
    .sort((a, b) => a.name.localeCompare(b.name))
}

/** A native select of the houses: keyboard-accessible, and honest about being a form control. */
export function HouseSelect({
  id,
  label,
  value,
  onChange,
  disabled,
  houses,
  exclude,
}: {
  id: string
  label: string
  value: string
  onChange: (id: string) => void
  disabled?: boolean
  houses: House[]
  /** the house the receipt is on already, left out of a Move picker */
  exclude?: string | null
}) {
  return (
    <>
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <select
        id={id}
        value={value}
        disabled={disabled}
        onChange={e => onChange(e.target.value)}
        className="h-8 max-w-56 rounded-input border border-border bg-surface2 px-2 text-sm text-text focus-visible:border-mint focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint disabled:opacity-50"
      >
        <option value="">Pick a house</option>
        {houses
          .filter(h => h.id !== exclude)
          .map(h => (
            <option key={h.id} value={h.id}>
              {h.name}
            </option>
          ))}
      </select>
    </>
  )
}

/**
 * Pin a receipt to a house, or skip it, and refresh what depends on it: the
 * receipt lists, the grid, and the receipt itself (CAP-496, CAP-497).
 */
export function useReceiptDecision(receiptId: string, houses: House[]) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const refresh = () => {
    // Every receipt list: unmatched, and the classification list, which shows the house too.
    void queryClient.invalidateQueries({ queryKey: ['receipts'] })
    void queryClient.invalidateQueries({ queryKey: ['supplies'] })
    void queryClient.invalidateQueries({ queryKey: ['receipt', receiptId] })
  }
  const failed = (title: string) => (error: Error) =>
    toast({ title, description: error.message, variant: 'destructive' })
  const match = useMutation({
    mutationFn: (propertyId: string) => api.attributeReceipt(receiptId, propertyId),
    onSuccess: (_data, propertyId) => {
      const house = houses.find(h => h.id === propertyId)
      toast({ title: `Receipt matched to ${house?.name ?? propertyId}` })
      refresh()
    },
    onError: failed('Could not match this receipt'),
  })
  const skip = useMutation({
    mutationFn: () => api.skipReceipt(receiptId),
    onSuccess: () => {
      toast({ title: 'Receipt skipped', description: 'Match it to a house later to undo.' })
      refresh()
    },
    onError: failed('Could not skip this receipt'),
  })
  return { match, skip, busy: match.isPending || skip.isPending }
}

export function UnmatchedReceipts() {
  const unmatched = useQuery({
    queryKey: ['receipts', 'unmatched'],
    queryFn: () => api.getUnmatchedReceipts(),
    staleTime: 60_000,
  })
  const houses = useHouses()

  if (unmatched.isPending) {
    return <p className="text-sm text-textFaint">Checking for unmatched receipts…</p>
  }
  if (unmatched.isError) {
    return <p className="text-sm text-danger">Could not load unmatched receipts.</p>
  }
  const { receipts, ignored } = unmatched.data
  const skipped = ignoredSentence(ignored)

  return (
    <div className="space-y-3">
      {receipts.length === 0 ? (
        <p className="text-sm text-textMuted">Every receipt is matched.</p>
      ) : (
        <ul className="divide-y divide-border">
          {receipts.map(r => (
            <UnmatchedRow key={r.id} receipt={r} houses={houses} />
          ))}
        </ul>
      )}
      {skipped && <p className="text-xs text-textFaint">{skipped}</p>}
    </div>
  )
}

function UnmatchedRow({ receipt: r, houses }: { receipt: Receipt; houses: House[] }) {
  const [choice, setChoice] = useState('')
  const { match, skip, busy } = useReceiptDecision(r.id, houses)
  const shop = r.retailer ?? r.source
  const when = formatDate(r.ordered_at.slice(0, 10))
  // Supplies by kind: a one-off's or an excluded line's category is just its kind.
  const tracked = r.items.filter(i => i.kind === 'consumable' && i.category)
  const oneOffs = r.items.filter(i => !(i.kind === 'consumable' && i.category))

  return (
    <li className="py-3" aria-label={`${shop} ${formatDate(r.ordered_at.slice(0, 10))}`}>
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-sm font-medium text-text">
              {shop} · {formatDate(r.ordered_at.slice(0, 10))} · ${r.total.toFixed(2)}
            </span>
            <span className="text-xs text-textFaint">{reasonLabel(r.attribution_reason)}</span>
          </div>
          <p className="text-xs text-textMuted">{shipTo(r)}</p>
          <div className="flex flex-wrap gap-1">
            {tracked.map((i, idx) => (
              <Badge
                key={idx}
                variant="default"
                className="max-w-full whitespace-normal font-normal"
                title={i.name_raw}
              >
                {i.qty > 1 ? `${i.qty} × ` : ''}
                {shortItemName(i.name_raw).name}
                <span className="ml-1 text-textFaint">{categoryLabel(i.category!)}</span>
              </Badge>
            ))}
            {oneOffs.map((i, idx) => (
              <Badge
                key={`o${idx}`}
                variant="secondary"
                className="max-w-full whitespace-normal font-normal"
                title={i.name_raw}
              >
                {i.qty > 1 ? `${i.qty} × ` : ''}
                {shortItemName(i.name_raw).name}
              </Badge>
            ))}
          </div>
          {r.raw_text && (
            <details className="text-xs">
              <summary className="cursor-pointer text-textFaint hover:text-textMuted">
                Receipt text
              </summary>
              <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap rounded-sm bg-surface2 p-2 text-[11px] text-textMuted">
                {r.raw_text}
              </pre>
            </details>
          )}
        </div>
        <form
          className="flex items-center gap-2"
          onSubmit={e => {
            e.preventDefault()
            if (choice) match.mutate(choice)
          }}
        >
          <HouseSelect
            id={`match-${r.id}`}
            label={`House for ${shop} ${when}`}
            value={choice}
            onChange={setChoice}
            disabled={busy}
            houses={houses}
          />
          <Button type="submit" size="sm" disabled={!choice || busy}>
            {match.isPending ? 'Matching…' : 'Match'}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => skip.mutate()}
            aria-label={`Skip ${shop} ${when}`}
            title="Not supplies for any house: stop showing it here"
          >
            {skip.isPending ? 'Skipping…' : 'Skip'}
          </Button>
        </form>
      </div>
    </li>
  )
}
